import { memo, useCallback, useMemo } from 'react';
import { ErrorLoad, StepCSSTransitionGroup, Table, TableBody } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { getMemberTooltipContent as getMemberTooltipContentHelper } from '~/helpers/tooltip';
import {
  ClanIsDisbandedTooltip,
  ClanMembersInfoMissingTooltip,
  MemberInfoMissingTooltip,
  MemberIsBannedTooltip,
  RankTooltipContent,
} from '~/tooltips';

import { Tooltip } from '~/UIKit/components';

import MembersTableHead from './MembersTableHead';
import MembersTableItem from './MembersTableItem';
import { AccumulativeClanResourceTooltipContent } from './Tooltips';

import type { BATTLE_TYPES } from '~/constants';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { IClanMember } from '~/types/declaration';

interface MembersTableProps {
  clanId: number;
  currentAccount: ICurrentAccountState;
  currentBattleType?: BATTLE_TYPES;
  disabledIds?: number[];
  displayCheckboxes?: boolean;
  error?: string;
  isContextMenuEnabled?: boolean;
  isFetching: boolean;
  members: IClanMember[];
  selectedMemberIds?: number[];
  sort: {
    field: string;
    isAsc: boolean;
  };

  stickyContainerId: string;
  stickyAppContainerId?: string;
  stickyScrollContainerId?: string;
  isActiveSticky?: boolean;

  onChangeCommanderClick?: (memberToDelegate?: IClanMember) => void;
  onChangeRoleClick?: (memberToChangeId?: number) => void;
  onReloadClick: () => void;
  onRemoveMembersClick?: (members?: IClanMember[]) => void;
  onThSortClick: (field: string, isAsc: boolean, hiddenSortingNames: string[]) => void;
  onTickAllMembers?: (members: number[]) => void;
  onTickMember?: (member: number) => void;

  openAccountProfile: (accountId: string) => void;
  openChatWindow: (accountId: string, nickname: string) => void;
}

const MembersTable = ({
  clanId,
  currentAccount,
  currentBattleType,
  disabledIds = [],
  displayCheckboxes,
  error = '',
  isContextMenuEnabled = true,
  isFetching,
  members,
  selectedMemberIds = [],
  sort,
  stickyContainerId,
  stickyAppContainerId,
  stickyScrollContainerId,
  isActiveSticky = true,
  onChangeCommanderClick,
  onChangeRoleClick,
  onReloadClick,
  onRemoveMembersClick,
  onThSortClick,
  onTickAllMembers = () => {},
  onTickMember = () => {},
  openAccountProfile,
  openChatWindow,
}: MembersTableProps) => {
  const isMember = useMemo(() => clanId === currentAccount.clanId, [clanId, currentAccount.clanId]);

  const getMemberById = useCallback((id: number) => members.find((m) => m.id === id), [members]);

  const handleTickAllMembers = useCallback(() => {
    const allMembersAvailableToSelectIds = members.filter((m) => !disabledIds.includes(m.id)).map((m) => m.id);
    onTickAllMembers(allMembersAvailableToSelectIds);
  }, [disabledIds, members, onTickAllMembers]);

  const getMemberTooltipContent = useCallback(
    (tooltipTip: React.ReactNode) => {
      if (!tooltipTip) {
        return null;
      }

      const memberId = Number(tooltipTip);
      const member = getMemberById(memberId);

      if (!member) {
        return null;
      }

      return getMemberTooltipContentHelper(
        member,
        currentAccount,
        isContextMenuEnabled,
        displayCheckboxes,
        disabledIds,
      );
    },
    [currentAccount, disabledIds, displayCheckboxes, getMemberById, isContextMenuEnabled],
  );

  const getRankTooltipContent = useCallback(
    (tooltipTip: React.ReactNode) => {
      if (!tooltipTip) {
        return null;
      }

      const memberId = Number(tooltipTip);
      const member = getMemberById(memberId);

      if (!member || member.seasonId === null || member.seasonRank === null) {
        return null;
      }

      return <RankTooltipContent rank={member.rank} seasonId={member.seasonId} seasonRank={member.seasonRank} />;
    },
    [getMemberById],
  );

  const getAccumulativeClanResourceTooltipContent = useCallback(
    (tooltipTip: React.ReactNode) => {
      if (!tooltipTip) {
        return null;
      }

      const memberId = Number(tooltipTip);
      const member = getMemberById(memberId);

      if (!member) {
        return null;
      }

      let leveling = member.leveling;
      let gainedResource = member.accumulativeClanResource;

      if (memberId === currentAccount.id) {
        leveling = currentAccount.leveling ?? 0;
        gainedResource = currentAccount.accumulativeClanResource ?? 0;
      }

      const needResource = settings.supply.clanLevelingToAccountLeveling[leveling];

      return <AccumulativeClanResourceTooltipContent needResource={needResource} gainedResource={gainedResource} />;
    },
    [currentAccount.accumulativeClanResource, currentAccount.id, currentAccount.leveling, getMemberById],
  );

  const isErrored = Boolean(error?.length);

  const membersList = () => {
    if (isErrored) {
      return <ErrorLoad isFlat message={error} onReloadClick={onReloadClick} />;
    }

    const rows = members.map((member) => (
      <MembersTableItem
        key={member.id}
        member={member}
        clanId={clanId}
        currentAccount={currentAccount}
        disabledCheckbox={Boolean(displayCheckboxes) && disabledIds.includes(member.id)}
        displayCheckboxes={displayCheckboxes}
        isMember={isMember}
        isSelectedMember={Boolean(displayCheckboxes) && selectedMemberIds.includes(member.id)}
        isContextMenuEnabled={isContextMenuEnabled}
        currentBattleType={currentBattleType}
        onChangeCommanderClick={onChangeCommanderClick}
        onChangeRoleClick={onChangeRoleClick}
        onRemoveMembersClick={onRemoveMembersClick}
        openAccountProfile={openAccountProfile}
        openChatWindow={openChatWindow}
        onTickMember={onTickMember}
      />
    ));

    return (
      <StepCSSTransitionGroup level={3}>
        <TableBody key="table-body">{rows}</TableBody>
      </StepCSSTransitionGroup>
    );
  };

  const allMembersIsChecked = useMemo(() => {
    return members.length === disabledIds.length + selectedMemberIds.length;
  }, [disabledIds.length, members.length, selectedMemberIds.length]);

  const allMembersIsDisabled = useMemo(
    () => disabledIds.length === members.length,
    [disabledIds.length, members.length],
  );

  return (
    <div>
      <Table isFetching={isFetching} isErrored={isErrored} stickyContainerId={stickyContainerId}>
        <MembersTableHead
          allMembersIsChecked={allMembersIsChecked}
          allMembersIsDisabled={allMembersIsDisabled}
          currentBattleType={currentBattleType}
          displayCheckboxes={displayCheckboxes}
          isActiveSticky={isActiveSticky && !isErrored}
          isMember={isMember}
          sort={sort}
          stickyAppContainerId={stickyAppContainerId}
          stickyContainerId={stickyContainerId}
          stickyScrollContainerId={stickyScrollContainerId}
          onThSortClick={onThSortClick}
          onTickAllMembers={handleTickAllMembers}
        />
        {membersList()}
      </Table>

      <ClanIsDisbandedTooltip />
      <ClanMembersInfoMissingTooltip />
      <MemberIsBannedTooltip />
      <MemberInfoMissingTooltip />

      <Tooltip id="member-tooltip" getContent={getMemberTooltipContent} />
      <Tooltip id="rank-tooltip" getContent={getRankTooltipContent} />
      <Tooltip id="accumulative-clan-resource-tooltip" getContent={getAccumulativeClanResourceTooltipContent} />
    </div>
  );
};

export default memo(MembersTable);
